import { useCallback, useEffect, useState } from "react";
import { downLoadInvoiceAction, getAllInvoicesAction, getAllInvoicesForDownloadAction, getTypesAndStatusAction, sendInvoiceEmailAction, initiateRefundAction, getRefundsByTicketIdAction, syncRefundStatusAction, downloadRefundCreditNoteAction } from "./Action";
import { useToast } from "@chakra-ui/react";

type InvoiceResponse = {
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    data: any[]; // Replace `any` with your actual invoice type if available
};

export const useInvoice = () => {
    const [allInvoices, setAllInvoices] = useState<InvoiceResponse | null>(null);
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(10)
    const [filters, setFilters] = useState({ type: "", paymentStatus: "", startDate: "", endDate: "", ticketId: "" })
    const [ticketIdInput, setTicketIdInput] = useState("")
    const [isLoading, setIsLoading] = useState(false)
    const [orderTypes, setOrderTypes] = useState<string[]>([])
    const [paymentStatuses, setPaymentStatuses] = useState<string[]>([])
    const toast = useToast();

    useEffect(() => {
        const fetchTypesAndStatus = async () => {
            const res = await getTypesAndStatusAction();
            if (res.status) {
                setOrderTypes(res.data.orderFor || []);
                setPaymentStatuses(res.data.paymentStatus || []);
            } else {
                // Fallback values if API fails
                setOrderTypes(["Ambulance", "Extra Charges", "Subscription", "doctor-consultation"]);
                setPaymentStatuses(["created", "failed", "paid", "pending"]);
            }
        };
        fetchTypesAndStatus();
    }, []);
    const fetchInvoices = useCallback(async () => {
        setIsLoading(true);
        const res = await getAllInvoicesAction({ page, limit, ...filters });
        if (res.status) {
            setAllInvoices(res.data);
        } else {
            alert(res.message);
        }
        setIsLoading(false);
    }, [page, limit, filters]);
    // Add dependencies here

    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);

    const generateInvoice = async ({ invoiceId }: { invoiceId: string }) => {
        try {
            setIsLoading(true);

            const res = await downLoadInvoiceAction(invoiceId);

            if (!res.status) {
                toast({
                    title: "Download failed",
                    description: res.message,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            const blob = new Blob([res.data.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);

            // Format timestamp in IST with 12-hour format and lowercase am/pm
            const istDate = new Date().toLocaleString("en-IN", {
                timeZone: "Asia/Kolkata",
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            });

            // Format to: 2025_06_27_16:02pm
            const timestamp = istDate
                .replace(/\//g, "_")          // 27/06/2025 → 27_06_2025
                .replace(", ", "_")           // , → _
                .replace(/:/g, "_")           // 04:32 → 04_32
                .replace(/ /g, "")            // Remove space before am/pm
                .toLowerCase();               // PM → pm

            const filename = `Invoice_${invoiceId}_${timestamp}.pdf`;

            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast({
                title: "Download started",
                description: "Your receipt PDF is being downloaded.",
                status: "info",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Unexpected Error",
                description: "Something went wrong while downloading the invoice.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };
    // return { allInvoices, page, limit, setPage, setLimit, filters, setFilters, orderTypes, paymentStatuses, isLoading, setIsLoading, generateInvoice };
    // const downloadBulkInvoices = async (invoiceIds: string[]) => {
    //     try {
    //         setIsLoading(true);
    //         const res = await downLoadBulkInvoicesAction(invoiceIds);
    //         if (!res.status) {
    //             toast({
    //                 title: "Download failed",
    //                 description: res.message,
    //                 status: "error",
    //                 duration: 3000,
    //                 isClosable: true,
    //             });
    //             return;
    //         }

    //         const blob = new Blob([res.data.data], { type: "application/zip" });
    //         const url = window.URL.createObjectURL(blob);
    //         const link = document.createElement("a");
    //         link.href = url;
    //         link.download = `Invoices_${new Date().getTime()}.zip`;
    //         document.body.appendChild(link);
    //         link.click();
    //         document.body.removeChild(link);
    //         window.URL.revokeObjectURL(url);

    //         toast({
    //             title: "Download started",
    //             description: "Your invoices are being downloaded.",
    //             status: "success",
    //             duration: 3000,
    //             isClosable: true,
    //         });
    //     } catch (error) {
    //         toast({
    //             title: "Unexpected Error",
    //             description: "Something went wrong.",
    //             status: "error",
    //             duration: 3000,
    //             isClosable: true,
    //         });
    //     } finally {
    //         setIsLoading(false);
    //     }
    // };
    const sendInvoiceOnEmail = async ({ invoiceId, email }: { invoiceId: string; email: string }) => {
        try {
            setIsLoading(true);
            const res = await sendInvoiceEmailAction(invoiceId, email);
            toast({
                title: res.status ? "Email Initiated" : "Email Failed",
                description: res.message,
                status: res.status ? "success" : "error",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Unexpected Error",
                description: "Something went wrong while sending the invoice email.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const searchByTicketId = () => {
        setFilters((prev) => ({ ...prev, ticketId: ticketIdInput }));
        setPage(1);
    };


    const [selectedInvoiceForRefund, setSelectedInvoiceForRefund] = useState<any>(null);
    const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
    const [selectedTicketForRefundDetails, setSelectedTicketForRefundDetails] = useState<string>("");
    const [refundDetailsList, setRefundDetailsList] = useState<any[]>([]);
    const [isRefundDetailsModalOpen, setIsRefundDetailsModalOpen] = useState(false);
    const [isRefundLoading, setIsRefundLoading] = useState(false);
    const [isRefundDetailsLoading, setIsRefundDetailsLoading] = useState(false);
    const [isSyncingRefund, setIsSyncingRefund] = useState(false);

    const openInitiateRefundModal = (invoice: any) => {
        setSelectedInvoiceForRefund(invoice);
        setIsRefundModalOpen(true);
    };

    const closeInitiateRefundModal = () => {
        setSelectedInvoiceForRefund(null);
        setIsRefundModalOpen(false);
    };

    const handleInitiateRefund = async (payload: { order_ref_id: string; amount?: number; reason?: string }) => {
        try {
            setIsRefundLoading(true);
            const res = await initiateRefundAction(payload);
            if (res.status) {
                toast({
                    title: "Refund Initiated",
                    description: `Refund initiated successfully. Refund ID: ${res.data?.provider_refund_id || res.data?._id}`,
                    status: "success",
                    duration: 4000,
                    isClosable: true,
                });
                closeInitiateRefundModal();
                fetchInvoices();
            } else {
                toast({
                    title: "Refund Failed",
                    description: res.message,
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                });
            }
        } catch (err: any) {
            toast({
                title: "Error",
                description: err?.message || "Something went wrong while initiating refund.",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsRefundLoading(false);
        }
    };

    const openRefundDetailsModal = async (ticketId: string) => {
        setSelectedTicketForRefundDetails(ticketId);
        setIsRefundDetailsModalOpen(true);
        setIsRefundDetailsLoading(true);
        try {
            const res = await getRefundsByTicketIdAction(ticketId);
            if (res.status) {
                setRefundDetailsList(Array.isArray(res.data) ? res.data : [res.data]);
            } else {
                setRefundDetailsList([]);
            }
        } catch (err) {
            setRefundDetailsList([]);
        } finally {
            setIsRefundDetailsLoading(false);
        }
    };

    const handleSyncRefund = async (refundId: string) => {
        try {
            setIsSyncingRefund(true);
            const res = await syncRefundStatusAction(refundId);
            if (res.status) {
                toast({
                    title: "Status Synced",
                    description: `Latest status from Razorpay: ${res.data?.refund_status || "synced"}`,
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                setRefundDetailsList((prev) =>
                    prev.map((r) => (r._id === refundId ? { ...r, ...res.data } : r))
                );
                fetchInvoices();
            } else {
                toast({
                    title: "Sync Failed",
                    description: res.message,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (err: any) {
            toast({
                title: "Error",
                description: "Failed to sync with Razorpay.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsSyncingRefund(false);
        }
    };

        const generateRefundCreditNote = async ({ refundId }: { refundId: string }) => {
        try {
            setIsLoading(true);
            const res = await downloadRefundCreditNoteAction(refundId);
            if (!res.status) {
                toast({
                    title: "Download failed",
                    description: res.message,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            const blob = new Blob([res.data?.data ?? res.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const istDate = new Date().toLocaleString("en-IN", {
                timeZone: "Asia/Kolkata",
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            });
            const timestamp = istDate
                .replace(/\//g, "_")
                .replace(", ", "_")
                .replace(/:/g, "_")
                .replace(/ /g, "")
                .toLowerCase();

            const filename = `CreditNote_${refundId}_${timestamp}.pdf`;
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast({
                title: "Download started",
                description: "Your credit note PDF is being downloaded.",
                status: "info",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Unexpected Error",
                description: "Something went wrong while downloading the credit note.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const downloadRefundInvoiceByTicket = async (ticketId: string) => {
        try {
            setIsLoading(true);
            const res = await getRefundsByTicketIdAction(ticketId);
            if (!res.status || !res.data) {
                toast({
                    title: "Refund Not Found",
                    description: "No refund records found for this ticket.",
                    status: "warning",
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }
            const refunds = Array.isArray(res.data) ? res.data : [res.data];
            const processedRefunds = refunds.filter((r: any) => r.refund_status === "processed");
            if (processedRefunds.length === 0) {
                toast({
                    title: "Credit Note Not Ready",
                    description: "Credit note is only available once the refund is successfully processed.",
                    status: "info",
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }
            if (processedRefunds.length === 1) {
                await generateRefundCreditNote({ refundId: processedRefunds[0]._id });
            } else {
                openRefundDetailsModal(ticketId);
                toast({
                    title: "Multiple Refunds",
                    description: "Multiple refunds found. Please select which credit note to download from the list.",
                    status: "info",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: "Unexpected Error",
                description: "Failed to download refund credit note.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

const clearTicketIdSearch = () => {
        setTicketIdInput("");
        setFilters((prev) => ({ ...prev, ticketId: "" }));
        setPage(1);
    };

    return {
        allInvoices, page, limit, setPage, setLimit, filters, setFilters, orderTypes, paymentStatuses, isLoading, setIsLoading, generateInvoice, sendInvoiceOnEmail, ticketIdInput, setTicketIdInput, searchByTicketId, clearTicketIdSearch, getAllInvoicesForDownloadAction,
        selectedInvoiceForRefund, isRefundModalOpen, openInitiateRefundModal, closeInitiateRefundModal, handleInitiateRefund, isRefundLoading,
        selectedTicketForRefundDetails, refundDetailsList, isRefundDetailsModalOpen, setIsRefundDetailsModalOpen, openRefundDetailsModal, isRefundDetailsLoading, isSyncingRefund, handleSyncRefund, generateRefundCreditNote, downloadRefundInvoiceByTicket
    };
};
